import { Button, Stack, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useState } from "react";

import AppIcon from "@/common/components/Icons/AppIcon";
import { SupplierDto } from "@/core/api/generated/v1";

import SupplierAutocomplete, { SupplierAutocompleteProps } from "./SupplierAutocomplete";
import SupplierCreateUpdate, { SupplierCreateUpdateProps } from "./SupplierCreateUpdate";
import SupplierCreateUpdateModal from "./SupplierCreateUpdateModal";

interface OwnProps {
  autocompleteProps: SupplierAutocompleteProps;
  createUpdateProps?: Pick<SupplierCreateUpdateProps, "defaultValues">;
  createFormPlacement?: "modal" | "page";
  onCreateStart?: () => void;
  onCreate: (newValue: SupplierDto) => void;
}

type Props = OwnProps;

export default function SupplierAutocompleteOrCreate({
  autocompleteProps,
  createUpdateProps,
  createFormPlacement = "modal",
  onCreateStart,
  onCreate,
}: Props) {
  const [isCreate, setIsCreate] = useState(false);
  const [defaultValues, setDefaultValues] = useState<
    SupplierCreateUpdateProps["defaultValues"] | undefined
  >(undefined);

  return (
    <Stack spacing={2}>
      {/* 1. Search */}
      {!isCreate && (
        <Box>
          <SupplierAutocomplete
            {...autocompleteProps}
            withCreate
            createOptionTitle={(inputValue) => `Create new supplier "${inputValue}"`}
            onCreate={(newOption) => {
              setDefaultValues({
                ...createUpdateProps?.defaultValues,
                nameOrEmail:
                  newOption?.inputValue ||
                  createUpdateProps?.defaultValues?.nameOrEmail ||
                  undefined,
              });
              setIsCreate(true);
              onCreateStart && onCreateStart();
            }}
          />
        </Box>
      )}

      {/* 2. Create */}
      {isCreate && (
        <Box>
          {isCreate && (
            <>
              {createFormPlacement === "modal" && (
                <SupplierCreateUpdateModal
                  open={isCreate}
                  onClose={() => setIsCreate(false)}
                  createUpdateProps={{
                    defaultValues: defaultValues,
                    onCreate: onCreate,
                    onSave: (newValue) => {
                      setIsCreate(false);
                    },
                  }}
                />
              )}

              {createFormPlacement === "page" && (
                <Stack spacing={1}>
                  <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
                    <Button
                      variant='text'
                      color='secondary'
                      size='small'
                      startIcon={<AppIcon of='goBack' />}
                      onClick={() => setIsCreate(false)}
                    >
                      Back to search
                    </Button>

                    <Typography component='div' variant='subtitle1'>
                      Create new supplier
                    </Typography>
                  </Stack>

                  <SupplierCreateUpdate
                    defaultValues={defaultValues}
                    onCreate={onCreate}
                    onSave={(newValue) => {
                      setIsCreate(false);
                    }}
                  />
                </Stack>
              )}
            </>
          )}
        </Box>
      )}
    </Stack>
  );
}
